import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Dumbbell,
    Plus,
    Edit,
    Trash2,
    Calendar,
    Clock,
    Target,
    Weight,
    RotateCcw,
    Save,
    X,
    Trophy,
    Medal,
    User,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { WOD, WODResult, ScoreType, getScoreTypeName } from '../../types/wod';
import { toast } from 'react-hot-toast';

const WODManagementPage: React.FC = () => {
    const { businessId } = useBusiness();
    const { currentUser } = useAuth();
    const [wods, setWods] = useState<WOD[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingWod, setEditingWod] = useState<WOD | null>(null);

    // Results viewing
    const [expandedWodId, setExpandedWodId] = useState<string | null>(null);
    const [wodResults, setWodResults] = useState<WODResult[]>([]);
    const [loadingResults, setLoadingResults] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        scoreType: 'time' as ScoreType
    });

    useEffect(() => {
        if (businessId) {
            loadWods();
        }
    }, [businessId]);

    const loadWods = async () => {
        if (!businessId) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'wods'),
                where('businessId', '==', businessId),
                orderBy('date', 'desc')
            );
            const snapshot = await getDocs(q);
            const wodsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date()
            })) as WOD[];
            setWods(wodsData);
        } catch (error) {
            console.error('Error loading WODs:', error);
            toast.error('Error al cargar los WODs');
        } finally {
            setLoading(false);
        }
    };

    const loadResultsForWod = async (wod: WOD) => {
        if (expandedWodId === wod.id) {
            setExpandedWodId(null);
            setWodResults([]);
            return;
        }

        setExpandedWodId(wod.id);
        setLoadingResults(true);
        try {
            const resultsQuery = query(
                collection(db, 'wodResults'),
                where('wodId', '==', wod.id),
                where('businessId', '==', businessId),
                orderBy('score', wod.scoreType === 'time' ? 'asc' : 'desc')
            );
            const snapshot = await getDocs(resultsQuery);
            const results = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as WODResult[];
            setWodResults(results);
        } catch (error) {
            console.error('Error loading results:', error);
            toast.error('Error al cargar resultados');
        } finally {
            setLoadingResults(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!businessId || !currentUser) return;

        try {
            if (editingWod) {
                // Update existing WOD
                await updateDoc(doc(db, 'wods', editingWod.id), {
                    title: formData.title,
                    date: formData.date,
                    description: formData.description,
                    scoreType: formData.scoreType,
                    updatedAt: serverTimestamp()
                });
                toast.success('WOD actualizado');
            } else {
                // Create new WOD
                await addDoc(collection(db, 'wods'), {
                    businessId,
                    title: formData.title,
                    date: formData.date,
                    description: formData.description,
                    scoreType: formData.scoreType,
                    isActive: true,
                    createdBy: currentUser.id,
                    createdAt: serverTimestamp()
                });
                toast.success('WOD creado');
            }

            setShowForm(false);
            setEditingWod(null);
            setFormData({
                title: '',
                date: new Date().toISOString().split('T')[0],
                description: '',
                scoreType: 'time'
            });
            loadWods();
        } catch (error) {
            console.error('Error saving WOD:', error);
            toast.error('Error al guardar el WOD');
        }
    };

    const handleEdit = (wod: WOD) => {
        setEditingWod(wod);
        setFormData({
            title: wod.title,
            date: wod.date,
            description: wod.description,
            scoreType: wod.scoreType
        });
        setShowForm(true);
    };

    const handleDelete = async (wodId: string) => {
        if (!confirm('¿Eliminar este WOD? También se borrarán los resultados asociados.')) return;

        try {
            // Delete WOD
            await deleteDoc(doc(db, 'wods', wodId));

            // Delete associated results
            const resultsQuery = query(
                collection(db, 'wodResults'),
                where('wodId', '==', wodId)
            );
            const resultsSnapshot = await getDocs(resultsQuery);
            for (const resultDoc of resultsSnapshot.docs) {
                await deleteDoc(doc(db, 'wodResults', resultDoc.id));
            }

            toast.success('WOD eliminado');
            loadWods();
        } catch (error) {
            console.error('Error deleting WOD:', error);
            toast.error('Error al eliminar el WOD');
        }
    };

    const getScoreIcon = (type: ScoreType) => {
        switch (type) {
            case 'time': return <Clock className="w-4 h-4" />;
            case 'reps': return <Target className="w-4 h-4" />;
            case 'weight': return <Weight className="w-4 h-4" />;
            case 'rounds': return <RotateCcw className="w-4 h-4" />;
        }
    };

    const isToday = (dateStr: string) => {
        return dateStr === new Date().toISOString().split('T')[0];
    };

    return (
        <>
            <Helmet>
                <title>Gestión de WODs - Admin</title>
            </Helmet>

            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                            <Dumbbell className="w-7 h-7 text-amber-500" />
                            WODs (Entrenamientos)
                        </h1>
                        <p className="text-gray-400 mt-1">
                            Publica el entrenamiento del día para que tus usuarios registren sus resultados
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingWod(null);
                            setFormData({
                                title: '',
                                date: new Date().toISOString().split('T')[0],
                                description: '',
                                scoreType: 'time'
                            });
                            setShowForm(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Nuevo WOD
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-lg border border-gray-700">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-white">
                                    {editingWod ? 'Editar WOD' : 'Nuevo WOD'}
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingWod(null);
                                    }}
                                    className="text-gray-400 hover:text-white"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Título del WOD
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="Ej: FRAN, Murph, 220101..."
                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Fecha
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Descripción del Entrenamiento
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Ej: 21-15-9&#10;Thrusters (43/30 kg)&#10;Pull-ups"
                                        rows={5}
                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Tipo de Puntuación
                                    </label>
                                    <select
                                        value={formData.scoreType}
                                        onChange={(e) => setFormData({ ...formData, scoreType: e.target.value as ScoreType })}
                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    >
                                        <option value="time">⏱️ Tiempo (menor es mejor)</option>
                                        <option value="reps">🔢 Repeticiones (mayor es mejor)</option>
                                        <option value="weight">🏋️ Peso (mayor es mejor)</option>
                                        <option value="rounds">🔄 Rondas (mayor es mejor)</option>
                                    </select>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowForm(false);
                                            setEditingWod(null);
                                        }}
                                        className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Save className="w-5 h-5" />
                                        {editingWod ? 'Guardar Cambios' : 'Crear WOD'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* WODs List */}
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
                    </div>
                ) : wods.length === 0 ? (
                    <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
                        <Dumbbell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">No hay WODs publicados</h3>
                        <p className="text-gray-400 mb-6">
                            Crea el primer entrenamiento del día para que tus usuarios puedan registrar sus resultados
                        </p>
                        <button
                            onClick={() => setShowForm(true)}
                            className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-colors"
                        >
                            Crear Primer WOD
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {wods.map((wod) => (
                            <div
                                key={wod.id}
                                className={`bg-gray-900 rounded-xl p-5 border ${isToday(wod.date) ? 'border-amber-500/50 bg-gradient-to-r from-amber-900/20 to-transparent' : 'border-gray-800'}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-bold text-white">{wod.title}</h3>
                                            {isToday(wod.date) && (
                                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full font-medium">
                                                    HOY
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-800 text-gray-400 text-xs rounded-full">
                                                {getScoreIcon(wod.scoreType)}
                                                {getScoreTypeName(wod.scoreType).split(' ')[0]}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                                            <Calendar className="w-4 h-4" />
                                            {new Date(wod.date + 'T12:00:00').toLocaleDateString('es-ES', {
                                                weekday: 'long',
                                                day: 'numeric',
                                                month: 'long'
                                            })}
                                        </div>
                                        <p className="text-gray-300 whitespace-pre-line text-sm">
                                            {wod.description}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        <button
                                            onClick={() => handleEdit(wod)}
                                            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-colors"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(wod.id)}
                                            className="p-2 bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* View Results Button */}
                                <button
                                    onClick={() => loadResultsForWod(wod)}
                                    className="mt-4 w-full py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                    <Trophy className="w-4 h-4 text-amber-500" />
                                    Ver Resultados
                                    {expandedWodId === wod.id ? (
                                        <ChevronUp className="w-4 h-4" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4" />
                                    )}
                                </button>

                                {/* Expanded Results */}
                                {expandedWodId === wod.id && (
                                    <div className="mt-4 pt-4 border-t border-gray-800">
                                        {loadingResults ? (
                                            <div className="flex items-center justify-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500"></div>
                                            </div>
                                        ) : wodResults.length === 0 ? (
                                            <p className="text-center text-gray-500 py-4">
                                                No hay resultados registrados para este WOD
                                            </p>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-xs text-gray-500 mb-2">{wodResults.length} participantes</p>
                                                {wodResults.map((result, index) => (
                                                    <div
                                                        key={result.id}
                                                        className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg"
                                                    >
                                                        <div className={`w-6 h-6 flex items-center justify-center text-sm font-bold ${index === 0 ? 'text-yellow-400' :
                                                            index === 1 ? 'text-gray-300' :
                                                                index === 2 ? 'text-amber-600' : 'text-gray-500'
                                                            }`}>
                                                            {index < 3 ? <Medal className="w-5 h-5" /> : index + 1}
                                                        </div>
                                                        <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                                            {result.userPhotoUrl ? (
                                                                <img src={result.userPhotoUrl} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <User className="w-4 h-4 text-gray-400" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-white text-sm font-medium truncate">{result.userName}</p>
                                                            {result.notes && (
                                                                <p className="text-xs text-gray-500 truncate">{result.notes}</p>
                                                            )}
                                                        </div>
                                                        <div className="text-amber-400 font-bold">
                                                            {result.scoreDisplay}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default WODManagementPage;
